const DEFAULT_BIN_KEYS = ['20mm', '12mm', 'rsand', 'msand', 'csand'];
const MATERIAL_KEYS = ['20mm', '12mm', 'rsand', 'msand', 'csand'];

export const toNumber = (value) => {
  const parsed = parseFloat(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const SAND_MATERIAL_KEYS = ['rsand', 'msand', 'csand'];

export const pooledSandValue = (valuesByKey, materialKey) => {
  const value = toNumber(valuesByKey?.[materialKey]);
  if (value) return value;
  if (!SAND_MATERIAL_KEYS.includes(materialKey)) return 0;
  const sandValues = SAND_MATERIAL_KEYS.map((key) => toNumber(valuesByKey?.[key]));
  const nonZero = sandValues.filter((v) => v !== 0);
  return nonZero.length ? Math.max(...nonZero) : 0;
};

export const resolveMaterialKeyFromBinLabel = (label, fallbackKey) => {
  const raw = String(label || '').toLowerCase().trim();
  const name = raw.replace(/[^a-z0-9]/g, '');
  if (name.includes('20mm') || name === '20') return '20mm';
  if (name.includes('12mm') || name === '12') return '12mm';
  if (name.includes('msand') || (name.includes('ms') && name.includes('sand')) || name === 'sandd') return 'msand';
  if (name.includes('csand') || (name.includes('cs') && name.includes('sand'))) return 'csand';
  if (name.includes('rsand') || (name.includes('rs') && name.includes('sand'))) return 'rsand';
  if (name === 'sand') return 'msand';
  return fallbackKey;
};

export const buildMoistureLookup = (moistureRecord) => {
  const moisture = moistureRecord?.moisture || {};
  const absorption = moistureRecord?.absorption || {};
  const correction = moistureRecord?.correction_factor || {};

  return MATERIAL_KEYS.reduce(
    (acc, key) => {
      const moistureValue = toNumber(moisture[key]);
      const absorptionValue = toNumber(absorption[key]);
      acc.moisture[key] = moistureValue;
      acc.absorption[key] = absorptionValue;
      acc.correctionFactor[key] = Number(
        (correction[key] ?? moistureValue - absorptionValue).toFixed(2),
      );
      return acc;
    },
    { moisture: {}, absorption: {}, correctionFactor: {} },
  );
};

export const calculateCorrectedAggregateTarget = (designValue, correctionFactor) => {
  const design = toNumber(designValue);
  const cf = toNumber(correctionFactor);
  const correctionKg = Math.round(design * (cf / 100));
  return Math.ceil(design + correctionKg);
};

export const getBinMoistureDisplayRows = (plantConfig = {}, moistureRecord, recipeInputs = {}) => {
  const lookup = buildMoistureLookup(moistureRecord);
  const binConfigs = [
    { index: 1, enabled: plantConfig.is_bin_1, label: plantConfig.bin_1, aggKey: 'agg1' },
    { index: 2, enabled: plantConfig.is_bin_2, label: plantConfig.bin_2, aggKey: 'agg2' },
    { index: 3, enabled: plantConfig.is_bin_3, label: plantConfig.bin_3, aggKey: 'agg3' },
    { index: 4, enabled: plantConfig.is_bin_4, label: plantConfig.bin_4, aggKey: 'agg4' },
    { index: 5, enabled: plantConfig.is_bin_5, label: plantConfig.bin_5, aggKey: 'agg5' },
  ];

  return binConfigs.map((bin, arrayIndex) => {
    const materialKey = resolveMaterialKeyFromBinLabel(bin.label, DEFAULT_BIN_KEYS[arrayIndex]);
    const designValue = toNumber(recipeInputs[bin.aggKey]);
    const moisture = pooledSandValue(lookup.moisture, materialKey);
    const absorption = pooledSandValue(lookup.absorption, materialKey);
    const correctionFactor = pooledSandValue(lookup.correctionFactor, materialKey);
    const correctedTarget = calculateCorrectedAggregateTarget(designValue, correctionFactor);

    return {
      ...bin,
      materialKey,
      designValue,
      moisture,
      absorption,
      correctionFactor,
      correctedTarget,
    };
  });
};

export const calculateTotalMoistureCorrectionKg = (binRows = []) => {
  return (binRows || []).reduce((sum, bin) => {
    if (!bin?.enabled) return sum;
    const design = toNumber(bin?.designValue);
    const cf = toNumber(bin?.correctionFactor);
    return sum + Math.round(design * (cf / 100));
  }, 0);
};

export const correctedWaterAfterMoistureCorrection = (waterValue, totalCorrectionKg) => {
  const v = toNumber(waterValue);
  const total = toNumber(totalCorrectionKg);
  if (v > 0) return v - total;
  return v;
};

export const calculateWaterCementRatio = (totalWater, totalCement) => {
  const water = toNumber(totalWater);
  const cement = toNumber(totalCement);
  if (cement <= 0) return '0.00';
  return (water / cement).toFixed(2);
};

export const calculateCorrectedWaterTotal = (plantConfig = {}, recipeInputs = {}, totalCorrectionKg = 0) => {
  let total = 0;
  if (plantConfig.is_other_1) {
    total += correctedWaterAfterMoistureCorrection(recipeInputs.water1, totalCorrectionKg);
  }
  if (plantConfig.is_other_2) {
    total += correctedWaterAfterMoistureCorrection(recipeInputs.water2, totalCorrectionKg);
  }
  return total;
};

export const calculateWaterCementRatioAfterCorrection = (
  plantConfig = {},
  recipeInputs = {},
  totalCorrectionKg = 0,
) => {
  const correctedWater = calculateCorrectedWaterTotal(plantConfig, recipeInputs, totalCorrectionKg);
  return calculateWaterCementRatio(correctedWater, recipeInputs.total_cem);
};

export const buildDesignMixPopupPayload = (inputs = {}, selectedWorkSchedule = {}, moistureCorrection = null) => ({
  design_mix_list: [{
    cem1: inputs.cem1 || 0,
    cem2: inputs.cem2 || 0,
    cem3: inputs.cem3 || 0,
    cem4: inputs.cem4 || 0,
    cem5: inputs.cem5 || 0,
    agg1: inputs.agg1 || 0,
    agg2: inputs.agg2 || 0,
    agg3: inputs.agg3 || 0,
    agg4: inputs.agg4 || 0,
    agg5: inputs.agg5 || 0,
    water1: inputs.water1 || 0,
    water2: inputs.water2 || 0,
    adm1: inputs.adm1 || 0,
    adm2: inputs.adm2 || 0,
    others: inputs.others || 0,
    density: inputs.density || 0,
    water_cem: inputs.water_cem || 0,
    total_cem: inputs.total_cem || 0,
    moisture_correction: moistureCorrection,
    work_schedule: selectedWorkSchedule,
  }],
});

export const pickMoistureRecordForPlant = (records, plantId, plantAlias) => {
  const list = Array.isArray(records) ? records : [];
  if (!list.length) {
    return null;
  }

  const normalizedAlias = String(plantAlias || '').trim().toLowerCase();
  const byPlantId = list.filter((record) => Number(record.plant_id) === Number(plantId));
  const byExactAlias = list.filter(
    (record) => String(record.plant_name || '').trim().toLowerCase() === normalizedAlias,
  );
  const byFuzzyAlias = list.filter((record) => {
    const name = String(record.plant_name || '').trim().toLowerCase();
    return normalizedAlias && name && (name.includes(normalizedAlias) || normalizedAlias.includes(name));
  });

  // Always keep the latest plant MC, including an all-zero session.
  // Zero moisture is valid until the next moisture correction is created.
  const candidates = (
    byPlantId.length
      ? byPlantId
      : byExactAlias.length
        ? byExactAlias
        : byFuzzyAlias.length
          ? byFuzzyAlias
          : list
  );

  const sorted = [...candidates].sort((a, b) => Number(b.id || 0) - Number(a.id || 0));
  const latestActive = sorted.find((record) => record.is_active !== false);
  return latestActive || sorted[0] || null;
};
